import { DatePipe } from '@angular/common';
import { Pipe, PipeTransform } from '@angular/core';
import moment from 'moment';
import { LocaleHelper } from 'src/app/l10n-config';
import { LogHelper } from '../../logging/log-helper';

export function fixDate(input: unknown): Date | null {
  if (input instanceof Date) {
    return input;
  }

  if (typeof input === 'string') {
    const m = moment(input);
    return m.isValid() ? m.toDate() : new Date('invalid');
  }

  return null;
}

export function timeZoneForDate(reference: Date = null, locale: string = 'en-US'): string {
  const ref = fixDate(reference || new Date());
  const timeString = ref.toLocaleTimeString(locale, { timeZoneName: 'short' });
  const parts = timeString.split(' ');
  return parts.at(-1);
}

export function formatDateTime(
  date: Date,
  locale: string = LocaleHelper.defaultLocale,
  includeTz: boolean = false,
  dateFormat: string = 'mediumDate',
  timeFormat: string = 'shortTime',
): string {
  const fixed = fixDate(date || new Date());
  const pipe = new SafeDatePipe(locale);
  let formatted = dateFormat ? pipe.transform(fixed, dateFormat) : '';
  if (timeFormat) {
    formatted += ', ';
    formatted += pipe.transform(fixed, timeFormat);
  }
  if (includeTz) {
    const timeZone = timeZoneForDate(fixed, locale);
    formatted += ' ' + timeZone;
  }

  return formatted;
}

@Pipe({
  name: 'fixDate',
  standalone: true
})
export class FixDatePipe implements PipeTransform {
  transform(value: unknown): Date | null {
    return fixDate(value);
  }
}

/** ROAD-13769 - Angular's DatePipe requires locale imports during app bootstrap (see LocaleHelper) */
@Pipe({
  name: 'date', // use the same name as Angular's DatePipe so that ours gets used in templates
  standalone: true,
})
export class SafeDatePipe extends DatePipe implements PipeTransform {
  private static readonly failedLocales = new Map<string, unknown>();
  static forDefaultLocale(): SafeDatePipe { return new SafeDatePipe(LocaleHelper.defaultLocale); }

  private static didLogError = false;
  private static didLogFallback = false;
  
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  transform(value: any, format?: string, timezone?: string, locale?: string): any {
    const defaultLocale = LocaleHelper.defaultLocale;

    // if no locale is provided, use a known safe one
    if (!locale) locale = LocaleHelper.getSafeNgLocale();

    // DatePipe requires special setup for non en-US locales (beyond what our L10n localization service provides)
    // Keep track of localization failures and fall back to en-US if we've already failed once.
    if (SafeDatePipe.failedLocales.has(locale) || !LocaleHelper.isNgSupported(locale)) {
      if (!SafeDatePipe.didLogFallback) {
        SafeDatePipe.didLogFallback = true;
        LogHelper.debug('SafeDatePipe', `unsupported locale '${locale}' falling back to default`);
      }
      
      locale = defaultLocale;
    }
    
    try {
      return super.transform(value, format, timezone, locale);
    } catch (error) {
      const errMessage = (error as object)['message'] as string;
      // check to see if this is a localization error
      if (locale != defaultLocale && errMessage.includes('NG0701')) {
        if (!SafeDatePipe.didLogError) {
          SafeDatePipe.didLogError = true;
          console.error(`Failed to transform date '${value}' for locale '${locale}'`, error);
        }

        SafeDatePipe.failedLocales.set(locale, error);
        return super.transform(value, format, timezone, defaultLocale);
      } else {
        // shouldn't get here, but if we do, just re-throw the error
        throw error;
      }
    }
  }
}
